import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { StepperDialogComponent } from '@serviceplan/ui-kit';
import { AssetBasketService } from '@thehubz/client/asset-services';
import {
  AssetsGenerateUrlService,
  AssetUIModel,
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  PROMPTS,
  StepperModel,
  ValueListsService,
  WidthClass,
} from '@thehubz/client/core';
import { Collection, CollectionsApiService, CreateCollectionDto } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { lastValueFrom } from 'rxjs';
import { AssetCommonService } from './asset-common.service';

@Injectable({
  providedIn: 'root',
})
export class CollectionStepperDialogService {
  collectionStepperData!: StepperModel;
  brandingKey!: CustomClients;

  existingCollectionOption = {
    id: 'add2ExistingCollection',
    name: $localize`Add to existing collection`,
  };

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private router: Router,
    private dialog: MatDialog,
    private collectionAPIService: CollectionsApiService,
    private assetUrlService: AssetsGenerateUrlService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private assetBasketService: AssetBasketService,
    public assetCommonService: AssetCommonService,
    private valueListsService: ValueListsService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  async createStepperJSON(assetListForCollection: AssetUIModel[]) {
    const usersLikeMe = await lastValueFrom(this.valueListsService.listUsersLikeMe(['global.library'], false, true));
    const coverCountLimitCheck = !!this.coverCountLimit(assetListForCollection);
    this.collectionStepperData = {
      stepID: 'createNewCollection',
      stepLabel: $localize`Collection`,
      stepper: [
        {
          stepperId: '1',
          stepperName: $localize`Select collection`,
          stepperGroupName: 'selectCollection',
          selected: true,
          formFields: [
            {
              formTypeId: '2',
              formFieldName: 'selectType',
              fieldType: 'dropdown',
              fieldLabel: $localize`Select collection`,
              dropDownSearch: false,
              sortDataSource: false,
              placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
              inputType: '',
              validators: {
                required: true,
              },
              widthClass: WidthClass.W12,
              disable: false,
              functionCall: true,
              visible: true,
              options: [
                {
                  id: 'createNewCollection',
                  name: $localize`Create new collection`,
                },
              ],
            },
          ],
          actions: [
            {
              actionLabel: PROMPTS.LABELS.NEXT,
              actionType: 'next',
              goToStep: 2,
              actionTypePosition: 'right',
              icon: 'chevron_right',
              alignIcon: 'right',
              visible: false,
            },
          ],
        },
      ],
      collectiveStepper: [
        {
          stepID: 'createNewCollection',
          stepLabel: '',
          stepper: [
            {
              stepperId: '2',
              stepperName: $localize`Create new collection`,
              stepperGroupName: 'collectionMetadata',
              selected: false,
              hideHeader: true,
              visited: false,
              disable: true,
              formFields: [
                {
                  formTypeId: '1',
                  formFieldName: 'name',
                  fieldType: 'text',
                  fieldLabel: $localize`Name`,
                  placeholder: $localize`Name`,
                  inputType: 'text',
                  validators: {
                    required: true,
                  },
                  widthClass: WidthClass.WFull,
                  visible: true,
                  value: '',
                },
                {
                  formTypeId: '2',
                  formFieldName: 'description',
                  fieldType: 'textarea',
                  fieldLabel: $localize`Description`,
                  placeholder: $localize`Description`,
                  inputType: 'text',
                  validators: {
                    required: true,
                  },
                  widthClass: WidthClass.WFull,
                  visible: true,
                  value: '',
                },
                {
                  formTypeId: '3',
                  formFieldName: 'cover',
                  fieldType: 'dropdown',
                  fieldLabel: $localize`Cover`,
                  dropDownSearch: true,
                  inlineImage: true,
                  validators: {
                    required: false,
                  },
                  withImg: true,
                  imgPreviewUrl: '',
                  placeholder: $localize`Choose cover`,
                  widthClass: WidthClass.WFull,
                  options: coverCountLimitCheck
                    ? assetListForCollection.map((asset: AssetUIModel) => {
                        return { _id: asset._id, name: asset.name, imageUrl: asset.imageUrl };
                      })
                    : [],
                  value: !coverCountLimitCheck ? assetListForCollection[0]._id : '',
                  visible: coverCountLimitCheck,
                },
                {
                  formTypeId: '4',
                  formFieldName: 'owners',
                  fieldType: 'multiSelectChip',
                  fieldLabel: $localize`Owners`,
                  validators: {
                    required: false,
                  },
                  placeholder: $localize`Choose owners`,
                  widthClass: WidthClass.WFull,
                  options: usersLikeMe,
                  value: '',
                  visible: true,
                },
                {
                  formTypeId: '5',
                  formFieldName: 'keywords',
                  fieldType: 'matChipList',
                  fieldLabel: $localize`Tags`,
                  mandatory: true,
                  validators: {
                    required: this.brandingKey === CustomClients.Standard ? false : true,
                  },
                  placeholder: $localize`Enter tags`,
                  widthClass: WidthClass.WFull,
                  options: [],
                  value: [],
                  visible: true,
                },
              ],
              actions: [
                {
                  actionLabel: $localize`:button:Create`,
                  actionTypePosition: 'right',
                  actionType: 'createNewCollection',
                  icon: '',
                  alignIcon: 'right',
                  visible: false,
                },
                {
                  actionLabel: PROMPTS.LABELS.BACK,
                  actionType: 'back',
                  goToStep: 1,
                  actionTypePosition: 'left',
                  icon: 'chevron_left',
                  alignIcon: 'left',
                  visible: false,
                },
              ],
            },
          ],
        },
        {
          stepID: 'add2ExistingCollection',
          stepLabel: '',
          stepper: [
            {
              stepperId: '2',
              stepperName: $localize`Add to existing collection`,
              stepperGroupName: 'collectionMetadata',
              selected: false,
              visited: false,
              disable: true,
              hideHeader: true,
              formFields: [
                {
                  formTypeId: '1',
                  validators: {
                    required: true,
                  },
                  formFieldName: 'add2ExistingCollection',
                  fieldType: 'dropdown',
                  dropDownSearch: true,
                  inlineImage: true,
                  fieldLabel: $localize`Add to existing collection`,
                  placeholder: $localize`Choose collection`,
                  widthClass: WidthClass.W12,
                  withImg: true,
                  imgPreviewUrl: '',
                  options: [],
                  value: '',
                  visible: true,
                },
              ],
              actions: [
                {
                  actionLabel: $localize`Add to Collection`,
                  actionType: 'createNewCollection',
                  actionTypePosition: 'right',
                  icon: '',
                  alignIcon: 'right',
                  visible: false,
                },
                {
                  actionLabel: PROMPTS.LABELS.BACK,
                  actionType: 'back',
                  goToStep: 1,
                  actionTypePosition: 'left',
                  icon: 'chevron_left',
                  alignIcon: 'left',
                  visible: false,
                },
              ],
            },
          ],
        },
      ],
    };
    /**
     * Remove the "Add to existing collections" option from the dropdown when the user has no existing collections (https://app.clickup.com/t/32wunx0).
     */
    const filterObject = {
      filters: {
        ownerID: this.assetCommonService.currentUser.userData._id,
      },
    };
    const collectionList = await lastValueFrom(this.collectionAPIService.collectionsControllerFindAll(JSON.stringify(filterObject)));
    if (collectionList.result.length !== 0) {
      this.collectionStepperData.stepper[0].formFields && this.collectionStepperData.stepper[0].formFields[0]?.options?.push(this.existingCollectionOption);
    }
  }

  async openDialogForCollection(assetListForCollection: AssetUIModel[]) {
    await this.createStepperJSON(assetListForCollection);
    return new Promise((resolve, reject) => {
      const HEIGHT = '90%';
      const WIDTH = '90%';
      const dialogRef = this.dialog.open(StepperDialogComponent, {
        width: WIDTH,
        height: HEIGHT,
        maxWidth: '100vw',
        disableClose: true,
        panelClass: 'custom-dialog-container',
        data: { stepperData: this.collectionStepperData },
      });
      dialogRef.componentInstance.assetUrlService = this.assetUrlService;
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          resolve(result);
          result = result.data || result;
          if (result.selectCollection?.selectType.id == 'add2ExistingCollection') {
            this.collectionAPIService.collectionsControllerFindOne(result.collectionMetadata.add2ExistingCollection.id).subscribe((resp: Collection) => {
              const assetIDs = resp.assets?.map((asset: AnyType) => asset._id) || [];
              const newAssetIds = assetListForCollection.map((asset) => asset._id);
              const newCollectionAssets = assetIDs.length ? assetIDs.concat(newAssetIds) : newAssetIds;
              this.collectionAPIService
                .collectionsControllerUpdate(result.collectionMetadata.add2ExistingCollection.id, {
                  assets: [...new Set(newCollectionAssets)],
                })
                .subscribe(() => {
                  this.clientNotificationHandlerService.successNotification(
                    $localize`Assets have been added to existing collection  "${result.collectionMetadata.add2ExistingCollection.name}"`,
                  );
                  this.assetBasketService.clearMyBasket();
                  this.router.navigate(['/collections/my-collection']);
                });
            });
          } else {
            const payloadObj: CreateCollectionDto = {
              name: result.collectionMetadata.name,
              description: result.collectionMetadata.description,
              keywords: await this.assetCommonService.modifiedTags(result.collectionMetadata.keywords),
              assets: assetListForCollection.map((asset) => asset._id),
            };
            // if cover available create coverAssetID property
            if (result.collectionMetadata.cover) {
              // some large asset have imageUrl large data:image value, so deleting it before adding to pay load
              // this breaks the collection creation api with error 'request entity too large'
              delete result.collectionMetadata.cover.imageUrl;
              payloadObj.coverAssetID = result.collectionMetadata.cover;
            }
            // if owners available create ownerIDs property
            if (result.collectionMetadata.owners) {
              payloadObj.ownerIDs = result.collectionMetadata.owners;
            }
            // Allianz-collection custom metadata
            if (result.metadata) {
              payloadObj.customFields = {
                team: result.metadata.team,
                targetGroup: result.metadata.target,
                campaignYear: result.metadata.campaignYear,
                contactPerson: result.metadata.contactPerson,
                agency: result.metadata.agency,
                internalComments: result.metadata.internalComments,
              };
            }
            this.collectionAPIService.collectionsControllerCreate(payloadObj).subscribe({
              next: async (data) => {
                this.clientNotificationHandlerService.successNotification($localize`Collection "${data.name}" has been created`);
                this.assetBasketService.clearMyBasket();
                this.router.navigate(['/collections/my-collection']);
              },
              error: (err) => {
                reject(err);
                this.clientNotificationHandlerService.errorNotification(err);
              },
            });
          }
        }
      });
    });
  }

  coverCountLimit(assets: AssetUIModel[]): number {
    const coverLimit = 20;
    return assets.length <= coverLimit ? assets.length : 0;
  }
}
